/**
 * Run Agent Modal
 *
 * Modal component for executing an agent with optional JSON input.
 */
import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { NativeSelect as Select } from '../ui/select';
import { SchemaForm } from '@/components/schema-form/SchemaForm';
import { useAgent } from '@/hooks';
import {
  initialValue,
  isRenderableSchema,
  pruneEmpty,
} from '@/types/inputSchema';

import { JsonEditor, validateJson } from '../ui/json-editor';
import {
  useAgentExecutions,
  useExecuteAgent,
  useExecution,
  getExecutionStatusColor,
  getExecutionStatusIcon,
  formatDuration,
} from '../../hooks/useExecution';
import { ExecutionPriority, Execution } from '../../types/execution';

interface RunAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  agentName: string;
}

type ModalView = 'input' | 'running' | 'result';

const PRIORITY_OPTIONS: { value: ExecutionPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

export function RunAgentModal({
  isOpen,
  onClose,
  agentId,
  agentName,
}: RunAgentModalProps) {
  const [view, setView] = useState<ModalView>('input');
  const [inputJson, setInputJson] = useState('');
  const [formValue, setFormValue] = useState<Record<string, unknown>>({});
  // Power users can still hand-write JSON even when a form is available.
  const [useJsonMode, setUseJsonMode] = useState(false);
  const [priority, setPriority] = useState<ExecutionPriority>('NORMAL');
  const [isJsonValid, setIsJsonValid] = useState(true);
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  // Two very different failures share one error box: the request never left
  // (bad JSON), or it left and we could not show the result. Only the second
  // means an agent is actually running somewhere.
  const [runStartedButViewFailed, setRunStartedButViewFailed] = useState(false);
  // How long the run has been going. The running view showed a spinner and a
  // status word, so there was no way to tell a run that is working from one that
  // is wedged.
  const [elapsedMs, setElapsedMs] = useState(0);

  const executeAgent = useExecuteAgent();

  // The agent declares the shape of its own run input. Without one there is
  // nothing to render a form from, so the JSON editor stays the only option.
  const { data: agent } = useAgent(isOpen ? agentId : undefined);
  const schema = agent?.inputSchema;
  const hasForm = isRenderableSchema(schema);
  const showingForm = hasForm && !useJsonMode;

  // Start from the last run. An agent has no schedule to seed from the way a
  // workflow does, so this modal opened blank every time — even for an agent
  // you had just run with the same boards and the same CV. Re-running with a
  // tweak is the common case; typing it all again is not.
  const { data: recent } = useAgentExecutions(isOpen ? agentId : undefined, {
    limit: 1,
  });
  const lastInput = recent?.data?.[0]?.input as Record<string, unknown> | undefined;

  // Poll execution status while running
  const { data: execution } = useExecution(executionId || undefined, {
    refetchInterval: 2000,
    refetchWhileRunning: true,
  });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setView('input');
      setInputJson('');
      setFormValue({});
      setUseJsonMode(false);
      setPriority('NORMAL');
      setIsJsonValid(true);
      setExecutionId(null);
      setRunError(null);
    }
  }, [isOpen]);

  // Seed the form from the schema once it loads. Keyed on the schema itself so
  // it does not clobber what the user has already typed on every render.
  useEffect(() => {
    if (isOpen && isRenderableSchema(schema)) {
      setFormValue(initialValue(schema, lastInput ?? null));
    }
  }, [isOpen, schema, lastInput]);

  // Update view when execution status changes
  useEffect(() => {
    if (execution) {
      if (execution.status === 'PENDING' || execution.status === 'RUNNING') {
        setView('running');
      } else {
        setView('result');
      }
    }
  }, [execution?.status]);

  // Tick only while the run view is up, so a closed dialog is not holding an
  // interval open.
  useEffect(() => {
    if (view !== 'running') return;
    const started = Date.now();
    const id = window.setInterval(() => setElapsedMs(Date.now() - started), 1000);
    return () => window.clearInterval(id);
  }, [view, executionId]);

  const handleValidationChange = useCallback((valid: boolean) => {
    setIsJsonValid(valid);
  }, []);

  const handleRun = async () => {
    setRunError(null);
    setRunStartedButViewFailed(false);

    let input: Record<string, unknown> | undefined;
    if (showingForm) {
      // Blank fields are dropped rather than sent empty — an agent reading
      // {"seeking": ""} treats the key as answered and stops asking.
      input = pruneEmpty(formValue) as Record<string, unknown> | undefined;
    } else if (inputJson.trim()) {
      const validation = validateJson(inputJson);
      if (!validation.isValid) {
        // Was a bare `return`. The Run button is normally disabled while the
        // JSON is invalid, but that flag only updates once the editor has
        // reported back — so on the first click after a bad paste the button was
        // live, this branch returned, and the dialog just sat there: no run, no
        // error, nothing to explain it. Say what is wrong instead.
        setRunError(
          `The input is not valid JSON, so the run did not start. ${validation.error ?? ''}`.trim()
        );
        setRunStartedButViewFailed(false);
        return;
      }
      input = JSON.parse(inputJson);
    }

    try {
      const response = await executeAgent.mutateAsync({
        agentId,
        data: { input, priority },
      });
      // POST /agents/{id}/execute returns ExecutionResponse — the id is at the
      // TOP LEVEL, not under `execution`. Reading response.execution.id threw a
      // TypeError that the empty catch below swallowed, so the run started (202
      // + "execution started" toast) while the modal sat on the input view
      // forever. Accept either shape, and fail loudly if neither is there.
      const newExecutionId = response?.execution?.id ?? response?.id;
      if (!newExecutionId) {
        setRunStartedButViewFailed(true);
        throw new Error('The run started but no execution id came back.');
      }
      setExecutionId(newExecutionId);
      setElapsedMs(0);
      setView('running');
    } catch (err) {
      // Never swallow this. A silent catch here is what made the button look dead.
      setRunError(
        err instanceof Error ? err.message : 'Could not start the run. Please try again.'
      );
    }
  };

  const handleClose = () => {
    // Allow closing at any time
    onClose();
  };

  const handleRunAnother = () => {
    setView('input');
    setInputJson('');
    setExecutionId(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative z-50 w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="run-modal-title"
      >
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h3 id="run-modal-title" className="text-lg font-semibold text-gray-900">
              Run Agent
            </h3>
            <p className="text-sm text-gray-500">{agentName}</p>
          </div>
        </div>

        {/* Input View */}
        {view === 'input' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Configure the execution parameters and provide optional input data.
            </p>

            {/* Priority Selection */}
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value as ExecutionPriority)}
                disabled={executeAgent.isPending}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">
                Higher priority executions are processed first.
              </p>
            </div>

            {/* Input — a form when the agent declares its shape, JSON otherwise */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor={showingForm ? undefined : 'input-json'}>
                  {showingForm ? 'Input' : 'Input Data (JSON)'}
                </Label>
                {hasForm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto py-1 text-xs"
                    disabled={executeAgent.isPending}
                    onClick={() => {
                      // Carry the work across when switching, so neither
                      // direction silently discards what was typed.
                      if (!useJsonMode) {
                        setInputJson(
                          JSON.stringify(pruneEmpty(formValue) ?? {}, null, 2)
                        );
                      } else if (inputJson.trim() && validateJson(inputJson).isValid) {
                        setFormValue(
                          initialValue(schema!, JSON.parse(inputJson))
                        );
                      }
                      setUseJsonMode((m) => !m);
                    }}
                  >
                    {useJsonMode ? 'Use the form' : 'Edit as JSON'}
                  </Button>
                )}
              </div>

              {showingForm ? (
                <SchemaForm
                  schema={schema!}
                  value={formValue}
                  onChange={setFormValue}
                  disabled={executeAgent.isPending}
                />
              ) : (
                <>
                  <JsonEditor
                    value={inputJson}
                    onChange={setInputJson}
                    height={200}
                    disabled={executeAgent.isPending}
                    placeholder={'{\n  "prompt": "Hello, agent!"\n}'}
                    onValidationChange={handleValidationChange}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional. Provide input data for the agent to process.
                  </p>
                </>
              )}
            </div>

            {/* Anything that stops the run must be visible here. The previous
                version caught the error and rendered nothing, so the button
                looked dead while the run had actually started. */}
            {runError && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 p-3"
              >
                <h4 className="text-sm font-medium text-red-800 mb-1">
                  {runStartedButViewFailed
                    ? 'Could not open the run view'
                    : 'The run did not start'}
                </h4>
                <p className="text-sm text-red-700">{runError}</p>
                {/* Only say "it may still be running" when it actually might.
                    On a validation failure nothing was sent, and telling someone
                    to go check Executions for a run that never happened is worse
                    than saying nothing. */}
                {runStartedButViewFailed && (
                  <p className="text-xs text-red-600 mt-1">
                    The agent may still be running — check Executions.
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={executeAgent.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleRun}
                disabled={executeAgent.isPending || !isJsonValid}
              >
                {executeAgent.isPending ? (
                  <span className="flex items-center gap-2">
                    <LoadingSpinner />
                    Starting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <PlayIcon />
                    Run Agent
                  </span>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Running View */}
        {view === 'running' && execution && (
          <div className="space-y-4">
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                  <LoadingSpinner className="h-8 w-8 text-blue-600" />
                </div>
                <h4 className="text-lg font-medium text-gray-900">
                  Execution in Progress
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  Status: {execution.status}
                  {elapsedMs >= 1000 && (
                    <span className="ml-2 tabular-nums">
                      · {Math.floor(elapsedMs / 60000) > 0
                        ? `${Math.floor(elapsedMs / 60000)}:${String(Math.floor((elapsedMs % 60000) / 1000)).padStart(2, '0')}`
                        : `${Math.floor(elapsedMs / 1000)}s`}
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Execution ID: {execution.id}
                </p>
              </div>
            </div>

            {/* Input Preview */}
            {Object.keys(execution.input).length > 0 && (
              <div className="space-y-2">
                <Label>Input</Label>
                <pre className="bg-slate-100 p-3 rounded-md text-xs overflow-x-auto max-h-32">
                  {JSON.stringify(execution.input, null, 2)}
                </pre>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Result View */}
        {view === 'result' && execution && (
          <ExecutionResult
            execution={execution}
            onClose={handleClose}
            onRunAnother={handleRunAnother}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Execution Result Component
 */
function ExecutionResult({
  execution,
  onClose,
  onRunAnother,
}: {
  execution: Execution;
  onClose: () => void;
  onRunAnother: () => void;
}) {
  const isSuccess = execution.status === 'COMPLETED';
  const isFailed = execution.status === 'FAILED' || execution.status === 'TIMEOUT';

  return (
    <div className="space-y-4">
      {/* Status Badge */}
      <div className="flex items-center gap-3">
        <span className="text-2xl">{getExecutionStatusIcon(execution.status)}</span>
        <div>
          <span
            className={`px-3 py-1 text-sm rounded-full font-medium ${getExecutionStatusColor(
              execution.status
            )}`}
          >
            {execution.status}
          </span>
          {execution.duration !== null && (
            <span className="text-sm text-gray-500 ml-3">
              Duration: {formatDuration(execution.duration)}
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {isFailed && execution.error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-red-800 mb-1">Error</h4>
          <p className="text-sm text-red-700">{execution.error}</p>
        </div>
      )}

      {/* Output */}
      {isSuccess && execution.output && Object.keys(execution.output).length > 0 && (
        <div className="space-y-2">
          <Label>Output</Label>
          <pre className="bg-slate-100 p-4 rounded-md text-sm overflow-x-auto max-h-64">
            {JSON.stringify(execution.output, null, 2)}
          </pre>
        </div>
      )}

      {/* Input (collapsed by default) */}
      {Object.keys(execution.input).length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
            View Input Data
          </summary>
          <pre className="bg-slate-50 p-3 rounded-md text-xs overflow-x-auto max-h-32 mt-2">
            {JSON.stringify(execution.input, null, 2)}
          </pre>
        </details>
      )}

      {/* Metadata */}
      <div className="text-xs text-gray-500 space-y-1">
        <p>Execution ID: {execution.id}</p>
        {execution.startedAt && (
          <p>Started: {new Date(execution.startedAt).toLocaleString()}</p>
        )}
        {execution.completedAt && (
          <p>Completed: {new Date(execution.completedAt).toLocaleString()}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button type="button" onClick={onRunAnother}>
          Run Again
        </Button>
      </div>
    </div>
  );
}

/**
 * Loading Spinner Component
 */
function LoadingSpinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Play Icon Component
 */
function PlayIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
      />
    </svg>
  );
}

export default RunAgentModal;
